import Accessor from "@arcgis/core/core/Accessor";
import {
  property,
  subclass,
} from "@arcgis/core/core/accessorSupport/decorators";
import { when } from "@arcgis/core/core/reactiveUtils";
import PortalQueryParams from "@arcgis/core/portal/PortalQueryParams";
import { getLocale, normalizeMessageBundleLocale } from "@arcgis/core/intl";
import { EAppTemplateType } from "templates-common-library/baseClasses/CompatibilityChecker";
import Collection from "@arcgis/core/core/Collection";

export const specialLocaleLookup = {
  "de-ch": "de", // esri_de
  "de-de": "de", // esri_de
  "en-au": "en", // esri_en
  "en-ca": "en", // esri_en
  "en-gb": "en", // esri_en
  "en-us": "en", // esri_en
  "es-es": "es", // esri_es
  "es-mx": "es", // esri_es
  "fr-ch": "fr", // esri_fr
  "fr-fr": "fr", // esri_fr
  id: "ind", // esri_ind
  "it-ch": "it", // esri_it
  "it-it": "it", // esri_it
  "pt-br": "pt", // esri_pt
  "pt-pt": "po", // esri_po
  uk: "ukr", // esri_ukr
  "zh-hk": "hk", // esri_hk
  "zh-tw": "tw", // esri_tw
  "zh-cn": "zh", // esri_zh
};

@subclass("Landing.data.AppTypeInfo.AppTypeInfoViewModel")
export default class AppTypeInfoViewModel extends Accessor {
  @property()
  portal: __esri.Portal;

  @property()
  appTypeInfo: __esri.PortalItem[];

  @property()
  isAppTypeDataLoaded: boolean = false;

  @property()
  deprecatedAppList: __esri.Collection<EAppTemplateType> = new Collection([
    EAppTemplateType.Minimalist,
  ]);

  private _templateGroupQueryParts: string[] = [
    `owner:"esri_${this._getLocaleForUserQuery()}"`,
    `title:"Web Application Template"`,
  ];

  private _galTemplatesGroupQueryParts: string[] = [
    `owner:"esri_${this._getLocaleForUserQuery()}"`,
    `title:"Gallery Templates"`,
  ];

  private _itemQueryParts: string[] = [`tags:"instant mapping apps"`];

  constructor(params?: any) {
    super(params);

    when(
      () => this?.portal !== undefined,
      async () => {
        const queryResults = await Promise.all([
          this._queryGroupForTemplateApps(this._templateGroupQueryParts),
          this._queryGroupForTemplateApps(this._galTemplatesGroupQueryParts),
        ]);

        this.appTypeInfo = [...queryResults[0], ...queryResults[1]];
        this.appTypeInfo = this.appTypeInfo.filter(
          this._filterDeprecatedApps.bind(this)
        );
        this.isAppTypeDataLoaded = true;
      },
      {
        initial: true,
        once: true,
      }
    );
  }

  private _queryGroupForTemplateApps(
    queryParts: string[]
  ): Promise<__esri.PortalItem[]> {
    return new Promise(async (result, reject) => {
      var queryParams = new PortalQueryParams({
        query: queryParts.join(" AND "),
        disableExtraQuery: true, // stops orgId from being appended to query
        sortField: "modified",
        sortOrder: "desc",
        num: 100,
      } as any);

      const templateGroupResult: __esri.PortalQueryResult =
        await this.portal.queryGroups(queryParams);
      const groupRes = templateGroupResult.results;

      if (groupRes == null || groupRes.length === 0) {
        console.error("Could not find Instant App Data");
        reject();
      } else {
        const group: __esri.PortalGroup = groupRes[0];
        const templateApps: any = await group.queryItems({
          query: this._itemQueryParts.join(" AND "),
          disableExtraQuery: true,
          sortField: "modified",
          sortOrder: "desc",
          num: 100,
        } as any);

        result(templateApps.results);
      }
    });
  }

  private _getLocaleForUserQuery(): string {
    const retrievedLocale = getLocale();
    const locale = normalizeMessageBundleLocale(retrievedLocale as any);
    const lookupVal = specialLocaleLookup[locale.toLowerCase()];
    return lookupVal != null ? lookupVal : locale;
  }

  /** Returns false if substring of item.url is in _deprecatedAppList. Otherwise true.  */
  private _filterDeprecatedApps(item: __esri.PortalItem): boolean {
    return !this.deprecatedAppList
      .map((template) => {
        return item.url.includes(template);
      })
      .reduce((curr, acc) => {
        return curr || acc;
      }, false);
  }
}
